import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ContactDialogComponent } from '../contact-dialog/contact-dialog.component';
import { TranslateModule } from '@ngx-translate/core';
import { filter, takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../base-component';
import { AuthService } from '@features/auth/services/auth.service';
import { BusinessModeService } from '@core/services/business-mode.service';
import { LanguageService } from '@core/services/language.service';
import { ThemeService } from '@core/services/theme.service';
import { ActiveTimerService } from '@core/services/active-timer.service';
import { PipTimerService } from '@core/services/pip-timer.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { PlanGateComponent } from '../plan-gate/plan-gate.component';
import { AnnouncementBannerComponent } from '../announcement-banner/announcement-banner.component';
import { ItaReconnectBannerComponent } from '../ita-reconnect-banner/ita-reconnect-banner.component';
import { PlanService } from '@core/services/plan.service';
import { PlanType } from '@core/models/plan.model';
import { toSignal } from '@angular/core/rxjs-interop';

interface NavEntry {
  key: string;
  route: string[];
  labelKey: string;
  icon: string;       // Material icon name
  gated?: boolean;    // requires Professional plan; locked under Starter (Free)
  adminOnly?: boolean;
}

const NAV: NavEntry[] = [
  { key: 'dashboard',     route: ['/dashboard'],              labelKey: 'nav.dashboard',    icon: 'dashboard'   },
  { key: 'clients',       route: ['/clients'],                labelKey: 'nav.clients',      icon: 'group'       },
  { key: 'projects',      route: ['/projects'],               labelKey: 'nav.projects',     icon: 'folder'      },
  { key: 'tasks',         route: ['/tasks'],                  labelKey: 'nav.tasks',        icon: 'check_box'   },
  { key: 'time-tracking', route: ['/finance/time-tracking'],  labelKey: 'nav.timeTracking', icon: 'schedule',    gated: true },
  { key: 'invoices',      route: ['/finance/invoices'],       labelKey: 'nav.invoices',     icon: 'description' },
  // Was reachable only by drilling into the Dashboard revenue KPI card —
  // the route, TITLE_MAP entry and nav.transactions label all already
  // existed, it was simply never registered here.
  { key: 'transactions',  route: ['/finance/transactions'],   labelKey: 'nav.transactions', icon: 'payments'    },
  { key: 'contracts',     route: ['/contracts'],              labelKey: 'nav.contracts',    icon: 'gavel',       gated: true },
  { key: 'reports',       route: ['/finance/reports'],        labelKey: 'nav.reports',      icon: 'bar_chart'   },
  { key: 'admin',         route: ['/admin'],                  labelKey: 'nav.adminPanel',   icon: 'admin_panel_settings', adminOnly: true }
];

/**
 * `modeKey` is the business-mode label key (see business-mode.config.ts).
 * Without it the topbar title, breadcrumb and tab title stayed on the generic
 * term while the sidebar and page CTA were already remapped — so a legal
 * workspace showed "תיקים" in the nav and "פרויקטים" in the header.
 */
const TITLE_MAP: Record<string, { crumbKey: string; titleKey: string; modeKey?: string }> = {
  '/dashboard':             { crumbKey: 'nav.home',         titleKey: 'nav.dashboard'        },
  '/clients':               { crumbKey: 'nav.clients',      titleKey: 'nav.clients',      modeKey: 'clients'  },
  '/projects':              { crumbKey: 'nav.projects',     titleKey: 'nav.projects',     modeKey: 'projects' },
  '/tasks':                 { crumbKey: 'nav.tasks',        titleKey: 'nav.tasks',        modeKey: 'tasks'    },
  '/finance/time-tracking': { crumbKey: 'nav.timeTracking', titleKey: 'nav.timeTracking'     },
  '/finance/invoices':      { crumbKey: 'nav.invoices',     titleKey: 'nav.invoices'         },
  '/finance/receipts':      { crumbKey: 'nav.receipts',     titleKey: 'nav.receipts'         },
  '/finance/transactions':  { crumbKey: 'nav.transactions', titleKey: 'nav.transactions'     },
  '/finance/reports':       { crumbKey: 'nav.reports',      titleKey: 'nav.reports'          },
  '/contracts':             { crumbKey: 'nav.contracts',    titleKey: 'nav.contracts'        },
  '/profile':               { crumbKey: 'nav.profile',      titleKey: 'nav.profile'          },
  '/settings':              { crumbKey: 'nav.settings',     titleKey: 'nav.settings'         },
  '/admin':                 { crumbKey: 'nav.adminPanel',   titleKey: 'nav.adminPanel'       },
  '/business-settings':     { crumbKey: 'nav.businessSettings', titleKey: 'nav.businessSettings' }
};

@Component({
  selector: 'app-shell-v2',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatDialogModule,
    TranslateModule,
    NotificationBellComponent,
    PlanGateComponent,
    AnnouncementBannerComponent,
    ItaReconnectBannerComponent
  ],
  templateUrl: './shell-v2.component.html',
  styleUrls: ['./shell-v2.component.scss']
})
export class ShellV2Component extends BaseComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  readonly businessModeService = inject(BusinessModeService);
  readonly themeService = inject(ThemeService);
  readonly activeTimerSvc = inject(ActiveTimerService);
  readonly pipTimerSvc = inject(PipTimerService);
  private readonly languageService = inject(LanguageService);
  private readonly dialog = inject(MatDialog);

  private readonly planService = inject(PlanService);
  readonly user = this.authService.user;
  // Flipped to true on <img>'s error event so we fall back to initials
  // when the avatar URL is broken / 404 / blocked.
  readonly avatarBroken = signal(false);
  readonly currentUrl = signal<string>(this.router.url);
  // Mobile: sidebar slides in/out as a drawer driven by the hamburger.
  readonly mobileNavOpen = signal(false);
  toggleMobileNav(): void { this.mobileNavOpen.update(v => !v); }
  closeMobileNav(): void { this.mobileNavOpen.set(false); }
  readonly isFreePlan = toSignal(this.planService.usage$, { initialValue: this.planService.getCachedUsage() });
  readonly isFree = computed(() => {
    const u = this.isFreePlan();
    return u ? u.currentPlan === PlanType.Starter : false;
  });

  /** A nav item shows a lock glyph when it's gated and the user is on Free. */
  showLockOn(entry: NavEntry): boolean {
    return !!entry.gated && this.isFree();
  }

  readonly isAdmin = computed(() => this.user()?.role === 'Admin');

  readonly visibleNav = computed<NavEntry[]>(() => {
    const enabled = new Set(this.businessModeService.navItems());
    const admin = this.isAdmin();
    const baseFilter = enabled.size === 0
      ? NAV
      : NAV.filter(n => enabled.has(n.key) || n.adminOnly);
    return baseFilter.filter(n => !n.adminOnly || admin);
  });

  readonly modeName = computed<string>(() => {
    const m = this.businessModeService.businessMode();
    if (!m) return '';
    return m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
  });

  readonly userInitials = computed<string>(() => {
    const u = this.user();
    if (!u) return '?';
    const f = u.firstName?.[0] || '';
    const l = u.lastName?.[0] || '';
    return (f + l).toUpperCase() || u.email?.[0]?.toUpperCase() || '?';
  });

  readonly pageMeta = computed(() => {
    const url = this.currentUrl().split('?')[0];
    const exact = TITLE_MAP[url];
    if (exact) return this.applyModeLabel(exact);
    const prefix = Object.keys(TITLE_MAP).find(k => url.startsWith(k + '/'));
    if (prefix) return this.applyModeLabel(TITLE_MAP[prefix]);
    return { crumbKey: 'nav.home', titleKey: 'nav.dashboard' };
  });

  /** Swaps in the per-mode term (Cases / Work Orders / Customers) when the
   *  active business mode defines one, mirroring the sidebar's behaviour. */
  private applyModeLabel(entry: { crumbKey: string; titleKey: string; modeKey?: string }) {
    if (!entry.modeKey) return { crumbKey: entry.crumbKey, titleKey: entry.titleKey };
    return {
      crumbKey: this.resolveLabelKey(entry.modeKey, entry.crumbKey),
      titleKey: this.resolveLabelKey(entry.modeKey, entry.titleKey)
    };
  }

  constructor() {
    super();
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e) => {
      this.currentUrl.set((e as NavigationEnd).urlAfterRedirects);
    });
  }

  isActive(entry: NavEntry): boolean {
    const url = this.currentUrl().split('?')[0];
    const target = entry.route.join('/').replace(/^\/+/, '/');
    return url === target || url.startsWith(target + '/');
  }

  /** Override the i18n key with the per-mode label when one is registered. */
  resolveLabelKey(key: string, fallback: string): string {
    return this.businessModeService.getNavLabel(key, fallback);
  }

  /** Formatted elapsed time for the running-timer pill (HH:MM:SS). */
  formattedTimerTime(): string {
    const s = this.activeTimerSvc.elapsedSeconds();
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  }

  goNewInvoice(): void { this.router.navigate(['/finance/invoices/new']); }
  goNewClient(): void  { this.router.navigate(['/clients/new']); }
  goNewProject(): void { this.router.navigate(['/projects/new']); }
  goProfile(): void    { this.router.navigate(['/profile']); }
  goSettings(): void   { this.router.navigate(['/settings']); }
  // Business Settings (business name, address, tax details) was reachable from
  // the v1 nav user menu but was never carried over to this shell, leaving the
  // page findable only by drilling into /settings or following a notification.
  goBusinessSettings(): void { this.router.navigate(['/business-settings']); }

  // Support opens the contact dialog in place rather than routing anywhere — it
  // lives in the user menu because that is where people look for help, and a
  // profile sub-tab took two clicks and a guess to reach.
  openSupport(): void {
    this.dialog.open(ContactDialogComponent, {
      panelClass: 'contact-dialog-panel',
      maxWidth: '90vw',
      autoFocus: false
    });
  }

  logout(): void {
    this.authService.logout().pipe(takeUntil(this.destroy$)).subscribe();
  }

  openPipTimer(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.pipTimerSvc.isSupported()) return;
    this.pipTimerSvc.openPip().catch(err => console.error('PiP open failed:', err));
  }
}
